import type { MailHit } from "@kwp/contracts";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { errorMessage, useTRPC } from "../../lib/api";
import { Button, cx, ErrorText } from "../../ui";
import { BulkBar } from "./BulkBar";
import { accountColour, relativeDate } from "./model";
import { useSelection } from "./selection";
import { SelectBox } from "./ThreadList";
import { useBulk, type BulkTarget } from "./use-bulk";

/**
 * Search results replace the list (architecture §6.5): live from every mailbox in scope, merged
 * newest first, each labelled with its account. Accounts that could not be searched are named.
 * Results can be selected (shift-click for a range) and changed in bulk; "select everything
 * matching" makes a plan over the whole query, not only the loaded pages.
 */
export function SearchResults({
  query,
  scope,
  now,
  suggest,
  onOpen,
}: {
  query: string;
  scope: { workspaceId?: string; accountIds?: string[] };
  now: Date;
  suggest: (picker: "tag" | "move", accountIds: readonly string[]) => string[];
  onOpen: (hit: MailHit) => void;
}) {
  const trpc = useTRPC();
  const search = useInfiniteQuery(
    trpc.mail.search.infiniteQueryOptions(
      { query, ...scope, limit: 50 },
      { getNextPageParam: (page) => (page.nextCursor === null ? undefined : page.nextCursor), staleTime: 60_000 },
    ),
  );
  const data = search.data;
  const hits = useMemo(() => (data === undefined ? [] : data.pages.flatMap((p) => p.results)), [data]);
  const warnings = data === undefined ? [] : data.pages.flatMap((p) => p.warnings);
  const order = useMemo(() => hits.map((h) => h.id), [hits]);
  const selection = useSelection(JSON.stringify([query, scope]), order);
  const bulk = useBulk(selection.clear);
  const { sel } = selection;
  const picked = hits.filter((h) => sel.ids.has(h.id));
  const target: BulkTarget = sel.all
    ? { kind: "query", query, scope }
    : { kind: "selection", messageIds: picked.map((h) => h.id), conversations: new Set(picked.map((h) => h.threadId)).size };
  const allSelected = hits.length > 0 && picked.length === hits.length;

  return (
    <div className="flex flex-col gap-2">
      {picked.length > 0 || sel.all ? (
        <BulkBar
          count={picked.length}
          noun="message"
          allLoaded={allSelected}
          matching={sel.all}
          canMatch
          target={target}
          suggest={(picker) => suggest(picker, [...new Set(picked.map((h) => h.accountId))])}
          bulk={bulk}
          onSelectMatching={selection.selectMatching}
          onClear={selection.clear}
        />
      ) : null}
      <section aria-label="Search results" className="rounded-lg border border-border bg-surface">
        {search.isPending ? <p className="p-4 text-sm text-muted">Searching…</p> : null}
        {search.isError ? (
          <div className="p-4">
            <ErrorText>{errorMessage(search.error)}</ErrorText>
          </div>
        ) : null}
        {warnings.length > 0 ? (
          <ul aria-label="Search warnings" className="border-b border-border p-3 text-xs text-danger">
            {warnings.map((w) => (
              <li key={`${w.accountId}-${w.reason}`}>
                {w.address} could not be searched ({w.reason}).
              </li>
            ))}
          </ul>
        ) : null}
        {search.isSuccess && hits.length === 0 ? <p className="p-6 text-center text-sm text-muted">No messages match “{query}”.</p> : null}
        {hits.length > 0 ? (
          <>
            <label className="flex min-h-9 cursor-pointer items-center gap-3 border-b border-border px-3">
              <input type="checkbox" aria-label="Select all loaded results" checked={allSelected} onChange={selection.toggleAll} className="size-4 shrink-0 accent-accent" />
              <span className="text-xs text-muted">{picked.length > 0 ? `${picked.length} selected` : "Select"}</span>
            </label>
            <ul aria-label="Matching messages">
              {hits.map((h, index) => (
                <li key={h.id} className={cx("flex items-stretch", sel.ids.has(h.id) && "bg-surface-2")}>
                  <SelectBox label={`Select ${h.subject === "" ? "(no subject)" : h.subject}`} checked={sel.ids.has(h.id)} onToggle={(shift) => selection.toggle(index, shift)} />
                  <Hit hit={h} now={now} onOpen={() => onOpen(h)} />
                </li>
              ))}
            </ul>
          </>
        ) : null}
        {search.hasNextPage ? (
          <div className="flex justify-center p-3">
            <Button onClick={() => void search.fetchNextPage()} disabled={search.isFetchingNextPage}>
              {search.isFetchingNextPage ? "Loading…" : "More results"}
            </Button>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function Hit({ hit: h, now, onOpen }: { hit: MailHit; now: Date; onOpen: () => void }) {
  return (
    <button type="button" onClick={onOpen} className="flex w-full min-w-0 items-start gap-3 border-b border-border px-3 py-2 text-left hover:bg-surface-2">
      <span aria-hidden="true" className="mt-1.5 size-2.5 shrink-0 rounded-full" style={{ backgroundColor: accountColour(h.accountId) }} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex items-baseline gap-2">
          <span className={h.unread ? "min-w-0 flex-1 truncate text-sm font-semibold" : "min-w-0 flex-1 truncate text-sm"}>{h.from.name === null ? h.from.email : h.from.name}</span>
          <time dateTime={h.date.toISOString()} className="shrink-0 text-xs text-muted">
            {relativeDate(h.date, now)}
          </time>
        </span>
        <span className="truncate text-sm text-text">{h.subject === "" ? "(no subject)" : h.subject}</span>
        <span className="truncate text-xs text-muted">
          {h.accountAddress} · {h.snippet}
        </span>
      </span>
    </button>
  );
}
